/**
 * LINE OAuth フロー用のユーティリティ。
 *
 * ## なぜ cookie を使うか（sessionStorage からの変更理由）
 *
 * iPhone では LINE アプリの「自動ログイン」（Universal Links）が発動すると、
 * LINE 認可ページから戻るコールバックが **標準ブラウザの新規タブ** で開かれる。
 * sessionStorage はタブ固有のストレージであるため、新規タブ側では認可フロー開始時に
 * 保存した state を読み取れず、state 照合が必ず失敗していた。
 *
 * cookie はブラウザ全体（タブ間）で共有されるため、この問題を解消できる。
 *
 * ## セキュリティ設計
 *
 * - SameSite=Strict: クロスサイトリクエストへの cookie 付与を防ぐ
 * - Max-Age=600（10分）: サーバ側 line_login_sessions テーブルの TTL と同値
 * - state 照合は撤去禁止:
 *     code_verifier はサーバ DB に保存する本構成では、PKCE はログイン CSRF を
 *     防がない（code + code_verifier の組を攻撃者が揃えられるため）。
 *     cookie による user-agent 束縛（SameSite=Strict）+ サーバ側ワンタイム消費が
 *     CSRF 防御の実体であり、フロント側の state 照合はその補助検証として維持する。
 *
 * ## マルチタブ並行フローについて
 *
 * 複数タブで同時に LINE ログインを開始した場合、cookie は最後に保存した state で
 * 上書きされる（last-write-wins）。古いタブのコールバックは state 不一致で失敗する。
 * これは許容済みの動作である（同時並行ログインは想定しないユースケース）。
 */

import { deleteCookie, getCookie, setCookie } from '@company/utils/cookie'

/** cookie キー名。RFC 6265 では `:` を cookie-name に使えないため旧キーから変更 */
const STATE_KEY = 'line_oauth_state'

/** state の有効期限（秒）。サーバ側 line_login_sessions の TTL と合わせる */
const STATE_TTL_SECONDS = 600

// =============================================================================
// LINE 連携 after ログイン フラグ（cookie）
//
// /line-login/callback で LINE_NOT_LINKED が返ったとき、「ログインして連携する」を
// 押すと saveLineLinkAfterLogin() でこの cookie をセットして /login へ遷移する。
// /login 側は hasLineLinkAfterLogin() でバナーを表示し、ID/PW ログイン成功後に
// consumeLineLinkAfterLogin() で consume してから startLineLink へ進む。
//
// TTL は STATE_TTL_SECONDS（10分）と同値にする。フォームを長時間放置して TTL 切れ
// した場合は consumeLineLinkAfterLogin() が false を返し、通常のホーム遷移にフォールバックする。
// =============================================================================

/** LINE 連携フロー継続フラグの cookie キー名 */
const LINK_AFTER_LOGIN_KEY = 'line_link_after_login'

/**
 * /login 後に LINE 連携フローを再開するフラグを cookie に保存する。
 * /line-login/callback で LINE_NOT_LINKED だったとき、「ログインして連携する」ボタンが呼ぶ。
 */
export const saveLineLinkAfterLogin = (): void => {
  try {
    setCookie(LINK_AFTER_LOGIN_KEY, '1', { maxAgeSeconds: STATE_TTL_SECONDS })
  } catch {
    // cookie ブロック環境では書き込めない。消費時に false を返して通常遷移へフォールバックする
  }
}

/**
 * ログイン後連携フラグ cookie が存在するかを確認する（消費しない）。
 * /login の初期 state 計算（useState の lazy init）で使用する。
 */
export const hasLineLinkAfterLogin = (): boolean => {
  try {
    return getCookie(LINK_AFTER_LOGIN_KEY) === '1'
  } catch {
    return false
  }
}

/**
 * ログイン後連携フラグ cookie を消費する（読み取り後に削除する）。
 * cookie が '1' だった場合のみ true を返す。
 *
 * /login の onSubmit 成功時に呼び出す。フォームを長時間放置して TTL 切れした場合は
 * false を返し、呼び出し元が通常のホーム遷移へフォールバックする。
 * try/catch で cookie ブロック環境では false を返す。
 */
export const consumeLineLinkAfterLogin = (): boolean => {
  try {
    const value = getCookie(LINK_AFTER_LOGIN_KEY)
    deleteCookie(LINK_AFTER_LOGIN_KEY)
    return value === '1'
  } catch {
    return false
  }
}

/**
 * ログイン後連携フラグ cookie を削除する（キャンセル時に使用）。
 * /login のバナーで「連携をやめる」を押したときに呼ぶ。
 */
export const clearLineLinkAfterLogin = (): void => {
  try {
    deleteCookie(LINK_AFTER_LOGIN_KEY)
  } catch {
    // ignore
  }
}

// =============================================================================
// LINE 連携継続フラグ（メモリ変数）
//
// /login で ID/PW ログイン成功後、startLineLink の呼び出しと LINE 認可画面への
// window.location.assign の間に useUnAuthMiddleware が staff セット済みを検知して
// '/' へリダイレクトするのを抑止するためのメモリフラグ。
//
// cookie や sessionStorage にしない理由:
//   - 残留すると通常ログイン後の自動リダイレクトが恒久的に壊れるため
//   - メモリ変数であればページ遷移（assign による画面移動）で自然にリセットされる
//   - ページリロードで消えることが安全側への自然なフォールバックになる
// =============================================================================

/** /login → startLineLink → LINE 認可への遷移中を示すメモリフラグ */
let _lineLinkContinuationActive = false

/**
 * LINE 連携継続フラグを立てる。
 * /login の onSubmit 成功後、startLineLink を呼ぶ直前に markLineLinkContinuation() する。
 * useUnAuthMiddleware の '/' 自動リダイレクトを抑止する。
 */
export const markLineLinkContinuation = (): void => {
  _lineLinkContinuationActive = true
}

/**
 * LINE 連携継続フラグをクリアする。
 * startLineLink が失敗したとき（LINE 認可画面に遷移しない場合）に呼んで
 * フラグを落とし、ホームへの自動リダイレクトを再開させる。
 */
export const clearLineLinkContinuation = (): void => {
  _lineLinkContinuationActive = false
}

/**
 * LINE 連携継続フラグが有効かを返す。
 * useUnAuthMiddleware が staff 確定後の '/' リダイレクトを抑止する条件として参照する。
 */
export const isLineLinkContinuationActive = (): boolean => _lineLinkContinuationActive

/**
 * LINE OAuth の state を cookie に保存する。
 *
 * cookie がブロックされている環境（一部ブラウザの厳格プライバシーモード等）では
 * 書き込みに失敗するが、エラーは握りつぶす。コールバック時に state を読み取れない
 * 場合は context_lost として再試行導線を表示する。
 */
export const saveLineOAuthState = (state: string): void => {
  try {
    setCookie(STATE_KEY, state, { maxAgeSeconds: STATE_TTL_SECONDS })
  } catch {
    // cookie ブロック環境では書き込めない。コールバック時に context_lost として扱う
  }
}

/**
 * LINE OAuth の state を cookie から読み取り、削除して返す（1回限り消費）。
 * 存在しない場合は null を返す。
 *
 * deleteCookie の失敗はサーバ側の CAS ワンタイム消費（consumed_at セット）が
 * 二重交換を防ぐため、無視して構わない。
 */
export const consumeLineOAuthState = (): string | null => {
  try {
    const raw = getCookie(STATE_KEY)
    if (raw === null) return null
    const state = decodeURIComponent(raw)
    deleteCookie(STATE_KEY)
    return state
  } catch {
    return null
  }
}

/**
 * LINE 認可 URL に `disable_auto_login=true` を付与して返す。
 *
 * iPhone で LINE アプリが Universal Links を使って自動ログインしようとすると
 * コールバックが新規タブで開き、sessionStorage による state 照合が失敗していた
 * （cookie 移行で根本解決済み）。`disable_auto_login=true` はその自動ログインを
 * 抑制する LINE 公式の表示制御パラメータであり、二重の保護として付与する。
 *
 * このパラメータは LINE 認可画面の表示制御にのみ影響し、
 * サーバ側が保持する state / nonce / PKCE の整合性には一切影響しない。
 * そのため proto 拡張は不要でフロントのみで付与する。
 *
 * URL のパース・再構築に失敗した場合は元の URL をそのまま返す。
 */
export const appendDisableAutoLogin = (authorizeUrl: string): string => {
  try {
    const url = new URL(authorizeUrl)
    url.searchParams.set('disable_auto_login', 'true')
    return url.toString()
  } catch {
    return authorizeUrl
  }
}
